import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AvailableTabProperties } from '../../interface/station-management-side-panel.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setSelectedStationIds } from '../../redux/slice/StationComparisonSlice';
import { setSelectedStation } from '../../redux/slice/StationManagementSidePanelSlice';

import StationComparison from './components/StationComparison';
import StationDashboard from './components/StationDashboard';
import StationManagementTabs from './components/StationManagementTabs';
import StationPerformance from './components/StationPerformance';
import StationReports from './components/StationReports';
import StationTickets from './components/StationTickets';

/**
 * Station Management Side Panel
 * @returns {React.ReactNode} The station management side panel component
 */
function StationManagementSidePanel() {
  const { t, i18n } = useTranslation('stationManagement');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { tab: deviceTab } = useParams<{ tab: string }>();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const { id } = useParams();
  const [searchParameters] = useSearchParams();
  const { page } = useParams<{ page: string }>();

  const searchBy = searchParameters.get('searchBy'); // "New station"

  const containerTabs: AvailableTabProperties[] = useMemo(
    () => [
      {
        label: t('stationDashboard'),
        key: 'station-dashboard',
        isActive: deviceTab === 'station-dashboard' || !deviceTab,
      },
      {
        label: t('stationPerformance'),
        key: 'station-performance',
        isActive: deviceTab === 'station-performance',
      },
      {
        label: t('reports'),
        key: 'reports',
        isActive: deviceTab === 'reports',
      },
      {
        label: t('stationComparisonTab'),
        key: 'station-comparison',
        isActive: deviceTab === 'station-comparison',
      },
    ],
    [t, deviceTab, i18n.language],
  );

  /**
   * Function to switch the tab
   * @param {string} key The key of the tab to switch to
   * @param {boolean} navigateRequire Whether to navigate to the tab's route
   */
  const handleSwitchTab = (key: string, navigateRequire: boolean = true) => {
    if (!navigateRequire) return;
    const moduleName = location.pathname.split('/')[2];

    if (moduleName === 'station-management') {
      navigate({
        pathname: `/ev-admin/${moduleName}/${page}/station/${selectedChargingPoint?.id}/${key}`,
        search: `?searchBy=${selectedChargingPoint?.name}`,
      });
    } else {
      navigate({
        pathname: `/ev-admin/${moduleName}/station/${selectedChargingPoint?.id}/${key}`,
        search: `?searchBy=${selectedChargingPoint?.name}`,
      });
    }
  };
  useEffect(() => {
    if (!id) return;
    if (selectedChargingPoint?.id === id) return;
    dispatch(
      setSelectedStation({
        id,
        name: searchBy,
      }),
    );
  }, [id]);

  useEffect(() => {
    // Clear comparison list if we are not on the comparison tab
    if (deviceTab && deviceTab !== 'station-comparison') {
      dispatch(setSelectedStationIds([]));
    }

    return () => {
      // Also clear when the side panel is closed
      dispatch(setSelectedStationIds([]));
    };
  }, [deviceTab, dispatch]);

  return (
    <div className="station-management-side-panel-wrap" dir={i18n.dir()}>
      <div className="station-management-container">
        <StationManagementTabs containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />
        <div className="station-management-content">
          {(() => {
            const activeKey = containerTabs.find((tab) => tab.isActive)?.key || 'station-dashboard';

            let deviceContent = null;
            switch (activeKey) {
              case 'station-dashboard':
                deviceContent = <StationDashboard />;
                break;
              case 'station-performance':
                deviceContent = <StationPerformance />;
                break;
              case 'reports':
                deviceContent = <StationReports />;
                break;
              case 'station-comparison':
                deviceContent = <StationComparison />;
                break;
              case 'tickets':
                deviceContent = <StationTickets />;
                break;
              default:
                deviceContent = <StationDashboard />;
                break;
            }
            return deviceContent;
          })()}
        </div>
      </div>
    </div>
  );
}

export default StationManagementSidePanel;
